// =============================================================================
// PPIAplicacao — o dia da prova: vincular a prova, presença, QR e RANKING
// =============================================================================
// Fluxo: escolhe processo → escolhe vaga → (1) vincula a prova da vaga,
// (2) marca PRESENÇA dos convocados, (3) projeta o QR na sala, (4) depois vê o
// RANKING e DECIDE quem avança (é essa aprovação que dispara a Etapa 4).
//
// O ranking é CALCULADO na hora: anular questão ou corrigir gabarito (na aba
// Provas) reflete aqui imediatamente, sem reprocessar nada.

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { CheckCircle, XCircle, RotateCcw, QrCode, Trophy, Clock, FileText, X, UserCheck, UserPlus } from 'lucide-react';
import { CORES, BadgeStatus, Botao, estilos } from './ppiUI';
// Desistência (2026-08-06): também aqui — é comum a pessoa não aparecer no dia da
// prova e avisar depois. Sem isto, o RH só tinha "reprovar" para limpar a lista.
import {
    ModalDesistencia, MarcaDesistiu, BotaoDesistir, BotaoDesfazerDesistencia,
} from './PPIDesistencia';
// Inclusão posterior (2026-08-24): quem apareceu no dia sem estar na lista.
// É AQUI que a ação mora — a Aplicação é a tela do dia da prova, com o aplicador
// e a pessoa na frente um do outro.
import { ModalIncluirCandidato, MarcaIncluido } from './PPIInclusao';

const authHeaders = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('authToken')}`,
});

// Tom da badge de nível (mesmo padrão das outras telas do PPI).
const TOM_NIVEL = { 'Fácil': 'aprovado', 'Médio': 'emProcesso', 'Difícil': 'reprovado', 'Avançado': 'inscrito' };

// Média mínima para o candidato estar APTO à aprovação (espelha o NOTA_CORTE do
// backend). A decisão final segue humana — isto só destaca quem alcançou o corte.
const NOTA_CORTE = 7;

function PPIAplicacao() {
    const [processos, setProcessos] = useState([]);
    const [idProcesso, setIdProcesso] = useState('');
    const [vagas, setVagas] = useState([]);
    const [idVaga, setIdVaga] = useState('');
    const [provas, setProvas] = useState([]);
    const [dados, setDados] = useState(null);   // { vaga, prova, ranking }
    const [erro, setErro] = useState('');
    const [msg, setMsg] = useState('');
    const [marcandoTodos, setMarcandoTodos] = useState(false);
    // Prova corrigida de um candidato (abre num painel ao clicar no nome).
    const [detalhe, setDetalhe] = useState(null);       // { candidato, resumo, questoes }
    const [carregandoDet, setCarregandoDet] = useState(false);
    const [erroDet, setErroDet] = useState('');
    const [desistindo, setDesistindo] = useState(null);   // candidato no modal de desistência
    const [incluindo, setIncluindo] = useState(false);    // modal de inclusão posterior aberto
    // A SALA (2026-08-24): outros processos que entram NO MESMO QR. Guardado como
    // lista de ids — o processo selecionado acima está sempre dentro e não aparece
    // aqui (desmarcá-lo deixaria o QR sem dono).
    const [extrasNoQr, setExtrasNoQr] = useState([]);

    // Quantos ainda faltam marcar: elegíveis (não entregaram a prova) e sem presença.
    // É o número que o botão mostra — e é ele que faz o botão sumir quando a sala
    // inteira já foi chamada (botão que não tem o que fazer não deve existir).
    // Espelha o WHERE do endpoint; se os dois divergirem, o botão prometeria um
    // número e o servidor marcaria outro. ⚠️ Por isso o `!c.desistiu` entrou aqui
    // junto com o `STATUS <> 'Desistente'` do SQL_PRESENCA_EDITAVEL (2026-08-06).
    const porMarcar = useMemo(
        () => (dados?.ranking || []).filter((c) => !c.presenca && !c.respondeu && !c.desistiu).length,
        [dados]
    );

    // Processos + provas publicadas (para o seletor de vínculo)
    useEffect(() => {
        (async () => {
            try {
                const [rp, rv] = await Promise.all([
                    fetch('/api/treinamento/ppi/processos', { headers: authHeaders() }),
                    fetch('/api/treinamento/ppi/prova/provas', { headers: authHeaders() }),
                ]);
                if (rp.ok) setProcessos(await rp.json());
                if (rv.ok) setProvas((await rv.json()).filter((v) => v.STATUS === 'Publicado'));
            } catch (_) { /* */ }
        })();
    }, []);

    const carregarVagas = useCallback(async (id) => {
        setVagas([]); setIdVaga(''); setDados(null);
        if (!id) return;
        try {
            const r = await fetch(`/api/treinamento/ppi/processos/${id}`, { headers: authHeaders() });
            if (r.ok) setVagas((await r.json()).vagas);
        } catch (_) { /* */ }
    }, []);

    const carregarRanking = useCallback(async (id) => {
        if (!id) { setDados(null); return; }
        setErro('');
        try {
            const r = await fetch(`/api/treinamento/ppi/prova/vaga/${id}/ranking`, { headers: authHeaders() });
            if (!r.ok) throw new Error('Erro ao carregar o ranking.');
            setDados(await r.json());
        } catch (e) { setErro(e.message); }
    }, []);

    // Vincula (ou desvincula) a prova de UMA vaga — chamado por linha da tabela do
    // processo. Atualiza a linha local sem recarregar tudo, e reflete no ranking
    // se a vaga vinculada é a que está aberta embaixo.
    const vincularProvaVaga = async (idVagaRow, idProva) => {
        setErro(''); setMsg('');
        try {
            const r = await fetch(`/api/treinamento/ppi/vagas/${idVagaRow}`, {
                method: 'PUT', headers: authHeaders(),
                body: JSON.stringify({ idProva: idProva || null }),
            });
            // ⚠️ A mensagem do servidor é a que importa aqui: desde 2026-09-17 o
            // backend RECUSA trocar a prova de vaga já aplicada (ver
            // `server/treinamento/ppi/ppi-vaga-prova.js`) e explica o porquê. Um
            // "Erro ao vincular a prova." genérico esconderia justamente a
            // explicação que faz o usuário procurar a equipe de dados.
            if (!r.ok) {
                const corpo = await r.json().catch(() => ({}));
                throw new Error(corpo.message || 'Erro ao vincular a prova.');
            }
            setVagas((vs) => vs.map((v) => (v.ID === idVagaRow ? { ...v, ID_PROVA: idProva || null } : v)));
            if (String(idVagaRow) === String(idVaga)) carregarRanking(idVaga);
        } catch (e) { setErro(e.message); }
    };

    // Outros processos que PODEM dividir a mesma sala: os que ainda estão em curso
    // e cuja prova não foi encerrada. Rascunho e Finalizado ficam de fora — não há
    // sala para eles —, e o processo já selecionado também (ele é a sala).
    const candidatosAoQr = useMemo(
        () => processos.filter((p) => String(p.ID) !== String(idProcesso)
            && !p.PROVA_ENCERRADA_EM
            && !['Rascunho', 'Finalizado'].includes(p.STATUS)),
        [processos, idProcesso]
    );

    const alternarExtra = (id) => setExtrasNoQr((atual) => (
        atual.includes(id) ? atual.filter((x) => x !== id) : [...atual, id]
    ));

    // Abre a tela de PROJEÇÃO (relógio + QR único) numa nova aba — é ela que vai no
    // telão. O processo selecionado vem SEMPRE primeiro; os extras, na ordem em que
    // foram marcados. Um processo só continua gerando a URL de antes ("/ppi-projecao/7").
    const abrirProjecao = () => window.open(
        `/ppi-projecao/${[idProcesso, ...extrasNoQr].join('-')}`, '_blank');
    const algumaVinculada = vagas.some((v) => v.ID_PROVA);

    const marcarPresenca = async (idCand, presente) => {
        try {
            const r = await fetch(`/api/treinamento/ppi/prova/candidato/${idCand}/presenca`, {
                method: 'PATCH', headers: authHeaders(), body: JSON.stringify({ presente }),
            });
            const d = await r.json();
            if (!r.ok) throw new Error(d.message);
            carregarRanking(idVaga);
        } catch (e) { setErro(e.message); }
    };

    // A CHAMADA DA SALA em um clique. O RH abre a sala com a lista na mão e, quase
    // sempre, todo mundo veio — eram 30-40 cliques, cada um recarregando o ranking
    // inteiro. Aqui é uma requisição só, e o servidor decide quem é elegível.
    //
    // Só MARCA. Não existe "desmarcar todos" (decisão da Julia): tirar presença é
    // sempre individual — uma pessoa específica não veio. Por isso é BOTÃO e não um
    // checkbox no cabeçalho: checkbox representa estado (é o da coluna, linha a
    // linha); isto é uma ação, e ação que só vai para um lado é botão.
    const marcarTodosPresentes = async () => {
        if (!window.confirm(
            `Marcar presença para ${porMarcar} candidato(s) que ainda não foram marcados?\n\n`
            + `Quem já está presente não é alterado.`
        )) return;
        setMarcandoTodos(true); setErro(''); setMsg('');
        try {
            const r = await fetch(`/api/treinamento/ppi/prova/vaga/${idVaga}/presenca/marcar-todos`, {
                method: 'POST', headers: authHeaders(),
            });
            const d = await r.json();
            if (!r.ok) throw new Error(d.message);
            setMsg(d.message);
            await carregarRanking(idVaga);
        } catch (e) { setErro(e.message); }
        finally { setMarcandoTodos(false); }
    };

    const decidir = async (idCand, resultado) => {
        try {
            const r = await fetch(`/api/treinamento/ppi/prova/candidato/${idCand}/resultado`, {
                method: 'PATCH', headers: authHeaders(), body: JSON.stringify({ resultado }),
            });
            const d = await r.json();
            if (!r.ok) throw new Error(d.message);
            carregarRanking(idVaga);
        } catch (e) { setErro(e.message); }
    };

    // Abre a prova CORRIGIDA do candidato (níveis + acertou/errou/branco).
    const verProva = async (idCand) => {
        setCarregandoDet(true); setErroDet(''); setDetalhe(null);
        try {
            const r = await fetch(`/api/treinamento/ppi/prova/candidato/${idCand}/prova-detalhe`, { headers: authHeaders() });
            const d = await r.json();
            if (!r.ok) throw new Error(d.message || 'Erro ao carregar a prova do candidato.');
            setDetalhe(d);
        } catch (e) { setErroDet(e.message); }
        finally { setCarregandoDet(false); }
    };

    // Baixa o ranking em PDF. GET autenticado → blob (um <a href> não passaria o
    // token), abre em nova aba para conferir/imprimir.
    const baixarPdf = async () => {
        setErro('');
        try {
            const r = await fetch(`/api/treinamento/ppi/prova/vaga/${idVaga}/ranking/pdf`, { headers: authHeaders() });
            if (!r.ok) throw new Error('Erro ao gerar o PDF do ranking.');
            const blob = await r.blob();
            window.open(URL.createObjectURL(blob), '_blank');
        } catch (e) { setErro(e.message); }
    };

    return (
        <div>
            {/* Seletores */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '14px', marginBottom: '20px' }}>
                <div>
                    <label style={estilos.rotuloFiltro}>Processo</label>
                    <select style={estilos.input} value={idProcesso}
                        onChange={(e) => {
                            setIdProcesso(e.target.value);
                            setExtrasNoQr([]);   // a sala é do processo escolhido; trocar de processo desfaz a sala
                            carregarVagas(e.target.value);
                        }}>
                        <option value="">Selecione…</option>
                        {processos.map((p) => <option key={p.ID} value={p.ID}>{p.NOME_PROCESSO}</option>)}
                    </select>
                </div>
                <div>
                    <label style={estilos.rotuloFiltro}>Vaga</label>
                    <select style={estilos.input} value={idVaga} disabled={!vagas.length}
                        onChange={(e) => { setIdVaga(e.target.value); carregarRanking(e.target.value); }}>
                        <option value="">Selecione…</option>
                        {vagas.map((v) => <option key={v.ID} value={v.ID}>{v.CARGO}</option>)}
                    </select>
                </div>
            </div>

            {erro && <p style={{ color: '#be123c' }}>{erro}</p>}
            {msg && <div style={{ backgroundColor: '#ecfdf5', color: '#047857', padding: '10px 12px', borderRadius: '8px', fontSize: '13px', marginBottom: '12px' }}>{msg}</div>}

            {/* TABELA DE VAGAS DO PROCESSO — vincula cada vaga a uma prova de uma vez
                e gera o QR ÚNICO da sala (relógio + QR na projeção). */}
            {idProcesso && vagas.length > 0 && (
                <div style={{ border: `1px solid ${CORES.borda}`, borderRadius: '12px', padding: '18px', marginBottom: '20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px', flexWrap: 'wrap' }}>
                        <QrCode size={18} color={CORES.primaria} />
                        <h4 style={{ fontSize: '16px', fontWeight: 800, color: CORES.texto, margin: 0 }}>Vagas & provas do processo</h4>
                        <div style={{ marginLeft: 'auto' }}>
                            <Botao icone={QrCode} onClick={abrirProjecao} disabled={!algumaVinculada}
                                title={algumaVinculada ? 'Abrir o telão da sala (relógio + QR)' : 'Vincule ao menos uma prova para gerar o QR'}>
                                Gerar QR Code
                            </Botao>
                        </div>
                    </div>

                    {/* A SALA (2026-08-24): dois processos aplicados no mesmo dia davam
                        dois QRs no telão — e dois QRs obrigam o aplicador a explicar de
                        viva voz quem escaneia qual. Marcando aqui, o MESMO QR atende os
                        dois: o CPF resolve o processo e a trilha.

                        Só aparece quando existe outro processo em curso — checkbox
                        sozinho, sem opção do outro lado, é ruído na tela. */}
                    {candidatosAoQr.length > 0 && (
                        <div style={caixaSala}>
                            <div style={{ fontSize: '12px', fontWeight: 800, color: CORES.textoSec, textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: '8px' }}>
                                Aplicar junto, no mesmo QR
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 20px' }}>
                                {candidatosAoQr.map((p) => (
                                    <label key={p.ID} style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', fontSize: '13px', cursor: 'pointer', color: CORES.texto }}>
                                        <input
                                            type="checkbox"
                                            checked={extrasNoQr.includes(p.ID)}
                                            onChange={() => alternarExtra(p.ID)}
                                            style={{ width: '16px', height: '16px', accentColor: CORES.primaria, cursor: 'pointer', margin: 0 }}
                                        />
                                        {p.NOME_PROCESSO}
                                        <span style={{ color: CORES.textoFraco }}>· {p.STATUS}</span>
                                    </label>
                                ))}
                            </div>
                            <p style={{ fontSize: '12px', color: CORES.textoFraco, margin: '9px 0 0 0', lineHeight: 1.5 }}>
                                Um QR só para a sala inteira. Cada processo mantém a sua prova, o seu ranking e a sua
                                triagem — o que fica junto é apenas a <strong>aplicação</strong>. Vincule as provas de
                                cada processo na sua própria aba antes de projetar.
                            </p>
                        </div>
                    )}

                    <div style={estilos.tabelaWrap}>
                        <table style={estilos.tabela}>
                            <thead><tr>
                                <th style={estilos.th}>Cargo</th>
                                {/* "Convocados", não "Candidatos": no dia da prova o que
                                    importa é quem a triagem aprovou. O total de inscritos
                                    fica ao lado, em cinza, porque ele explica o número. */}
                                <th style={estilos.th}>Convocados</th>
                                <th style={estilos.th}>Prova vinculada</th>
                            </tr></thead>
                            <tbody>
                                {vagas.map((v) => (
                                    <tr key={v.ID}>
                                        <td style={{ ...estilos.td, fontWeight: 700 }}>{v.CARGO}</td>
                                        {/* Aprovados na triagem (fora os desistentes) — é
                                            quem senta na sala. O total de inscritos vem
                                            junto, em cinza: sem ele o número fica sem régua. */}
                                        <td style={{ ...estilos.td, fontVariantNumeric: 'tabular-nums' }}>
                                            {v.QTD_APROVADOS == null ? '—' : (
                                                <>
                                                    <strong>{v.QTD_APROVADOS}</strong>
                                                    <span style={{ color: CORES.textoFraco, fontWeight: 400 }}>
                                                        {' '}de {v.QTD_CANDIDATOS ?? 0} inscrito(s)
                                                    </span>
                                                </>
                                            )}
                                        </td>
                                        <td style={estilos.td}>
                                            <select style={{ ...estilos.input, minWidth: '260px' }} value={v.ID_PROVA || ''}
                                                onChange={(e) => vincularProvaVaga(v.ID, e.target.value)}>
                                                <option value="">— nenhuma —</option>
                                                {provas.map((p) => (
                                                    <option key={p.ID} value={p.ID}>
                                                        {p.CODIGO ? `${p.CODIGO} · ${p.NOME}` : p.NOME}
                                                    </option>
                                                ))}
                                            </select>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <p style={{ fontSize: '12px', color: CORES.textoFraco, margin: '10px 0 0 0' }}>
                        Só provas <strong>publicadas</strong> aparecem. O QR é <strong>único</strong>: o candidato escaneia, informa o CPF e é
                        direcionado para a prova da vaga dele. Marque a <strong>presença</strong> na seção abaixo antes de aplicar.
                    </p>
                </div>
            )}

            {dados && (
                <>
                    {/* Resumo da prova da vaga (o vínculo é feito na tabela do processo, acima). */}
                    {dados.vaga.idProva && (
                        <p style={{ fontSize: '13px', color: CORES.textoSec, margin: '0 0 16px 0' }}>
                            <strong>Prova da vaga · {dados.vaga.cargo}:</strong> {dados.prova.qtdQuestoes} questão(ões)
                            {dados.prova.qtdAnuladas > 0 && (
                                <strong style={{ color: '#be123c' }}> · {dados.prova.qtdAnuladas} anulada(s) — não contam para ninguém</strong>
                            )}
                        </p>
                    )}

                    {/* Presença + Ranking */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                        <Trophy size={18} color={CORES.primaria} />
                        <h4 style={{ fontSize: '16px', fontWeight: 800, color: CORES.texto, margin: 0 }}>Convocados & Ranking</h4>
                        <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
                            {/* A CHAMADA DA SALA em um clique. Só aparece quando há alguém por
                                marcar: com a sala inteira já chamada, o botão não teria o que
                                fazer — e botão que não faz nada só confunde quem lê a tela. */}
                            {porMarcar > 0 && (
                                <Botao icone={UserCheck} onClick={marcarTodosPresentes} disabled={marcandoTodos}
                                    style={{ padding: '6px 12px', fontSize: '12px' }}>
                                    {marcandoTodos ? 'Marcando…' : `Marcar todos presentes (${porMarcar})`}
                                </Botao>
                            )}
                            {/* Quem apareceu no dia e não está na lista (2026-08-24).
                                Fica junto da chamada da sala porque é o mesmo momento:
                                o aplicador com a lista na mão e a pessoa na frente. */}
                            <Botao variant="secundario" icone={UserPlus} onClick={() => setIncluindo(true)}
                                title="Incluir na prova alguém que não está na lista"
                                style={{ padding: '6px 12px', fontSize: '12px' }}>
                                Incluir candidato
                            </Botao>
                            <Botao variant="secundario" icone={FileText} onClick={baixarPdf} style={{ padding: '6px 12px', fontSize: '12px' }}>Baixar PDF</Botao>
                            <Botao variant="secundario" icone={RotateCcw} onClick={() => carregarRanking(idVaga)} style={{ padding: '6px 12px', fontSize: '12px' }}>Atualizar</Botao>
                        </div>
                    </div>

                    {/* O DESEMPATE POR TEMPO DE CASA sempre existiu, mas era invisível — e ele
                        decide promoção. Quando de fato agiu (alguém empatou na nota e a ordem
                        saiu do tempo de empresa), o aviso vem destacado e as linhas afetadas
                        ficam marcadas. Sem empate, o aviso nem aparece: dizer "houve desempate"
                        quando não houve é ruído. */}
                    {dados.houveDesempate && (
                        <div style={{
                            display: 'flex', gap: '10px', alignItems: 'flex-start',
                            backgroundColor: '#fffbeb', border: '1px solid #fde68a',
                            borderRadius: '10px', padding: '12px 14px', marginBottom: '12px',
                        }}>
                            <Clock size={16} color="#b45309" style={{ flexShrink: 0, marginTop: '1px' }} />
                            <p style={{ margin: 0, fontSize: '13px', color: '#b45309', lineHeight: 1.5 }}>
                                <strong>Houve desempate por tempo de empresa.</strong> Candidatos com a{' '}
                                <strong>mesma nota</strong> foram ordenados por <strong>quem tem mais tempo de casa</strong> —
                                as linhas marcadas com <span style={{ fontWeight: 900 }}>⏱</span> são as que o desempate decidiu.
                                Confira a coluna <strong>Tempo de empresa</strong>.
                            </p>
                        </div>
                    )}

                    <div style={estilos.tabelaWrap}>
                        <table style={estilos.tabela}>
                            <thead><tr>
                                <th style={estilos.th}>#</th>
                                <th style={estilos.th}>Candidato</th>
                                <th style={estilos.th}>Loja</th>
                                <th style={estilos.th}>Tempo de empresa</th>
                                <th style={estilos.th}>Presença</th>
                                <th style={estilos.th}>Nota</th>
                                <th style={estilos.th}>Situação</th>
                                <th style={{ ...estilos.th, textAlign: 'center' }}>Status</th>
                            </tr></thead>
                            <tbody>
                                {dados.ranking.map((c) => (
                                    // Desistente fica em cinza claro — visível, mas sem o destaque
                                    // de quem ainda está no páreo. Ele MANTÉM a posição e a nota:
                                    // a nota é fato, e tirá-lo embaralharia a colocação dos outros.
                                    <tr key={c.id} style={{
                                        backgroundColor: c.desistiu ? '#f8fafc'
                                            : c.resultado === 'Aprovado' ? '#f0fdf4'
                                                : (c.desempatado ? '#fffbeb' : undefined),
                                    }}>
                                        <td style={{ ...estilos.td, fontWeight: 900, color: CORES.primaria, fontVariantNumeric: 'tabular-nums' }}>
                                            {c.respondeu ? c.posicao : '—'}
                                            {c.desempatado && (
                                                <span title="A posição foi definida pelo tempo de empresa (empate na nota)"
                                                    style={{ color: '#b45309', marginLeft: '3px' }}>⏱</span>
                                            )}
                                            {c.empateTotal && (
                                                <span title="Empate TOTAL: mesma nota e mesmo tempo de casa — o desempate não resolveu"
                                                    style={{ color: '#b45309' }}> =</span>
                                            )}
                                        </td>
                                        <td style={estilos.td}>
                                            {/* Nome clicável (só quem FEZ a prova) → abre a prova corrigida. */}
                                            {c.respondeu ? (
                                                <button onClick={() => verProva(c.id)} title="Ver a prova corrigida deste candidato"
                                                    style={btnNome}>
                                                    <strong>{c.nome}</strong>
                                                    <FileText size={13} style={{ flexShrink: 0 }} />
                                                </button>
                                            ) : (
                                                <strong>{c.nome}</strong>
                                            )}
                                            <div style={{ fontSize: '11px', color: CORES.textoFraco }}>{c.cargoAtual}</div>
                                            {/* ⚠️ A inclusão grava 'Aprovado' no MESMO campo da triagem
                                                (é o que faz os seis portões da prova funcionarem) — sem
                                                esta marca, quem entrou por fora da triagem seria
                                                indistinguível de quem passou nela. */}
                                            <MarcaIncluido inclusao={c.inclusao} />
                                        </td>
                                        <td style={estilos.td}>{c.loja || '—'}</td>
                                        <td style={{
                                            ...estilos.td, fontVariantNumeric: 'tabular-nums',
                                            fontWeight: c.desempatado ? 800 : 400,
                                            color: c.desempatado ? '#b45309' : CORES.texto,
                                        }}>
                                            {c.tempoEmpresaDias === null
                                                ? <span style={{ color: CORES.textoFraco }}>—</span>
                                                : <>{c.tempoEmpresaDias.toLocaleString('pt-BR')} <span style={{ fontSize: '11px', color: CORES.textoFraco, fontWeight: 400 }}>dias</span></>}
                                        </td>
                                        <td style={estilos.td}>
                                            {/* Checkbox de chamada: marcado = presente (verde). Mais intuitivo
                                                que a pílula antiga, que misturava estado e ação. Trava depois
                                                que a pessoa entregou a prova (a presença não muda mais). */}
                                            {/* Desistente não é chamado para a sala: a presença é o
                                                PORTEIRO da prova (marcada, a prova abre), e o servidor
                                                também recusa (409). */}
                                            <label
                                                title={c.desistiu ? 'Desistiu do processo — não é chamado para a prova'
                                                    : c.respondeu ? 'Já entregou a prova — presença travada'
                                                        : 'Marcar/desmarcar a presença na sala'}
                                                style={{
                                                    display: 'inline-flex', alignItems: 'center', gap: '7px',
                                                    cursor: (c.respondeu || c.desistiu) ? 'not-allowed' : 'pointer',
                                                    fontSize: '13px',
                                                    fontWeight: c.presenca ? 700 : 400,
                                                    color: c.presenca ? '#059669' : CORES.textoSec,
                                                    opacity: c.desistiu ? 0.5 : 1,
                                                }}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={c.presenca}
                                                    disabled={c.respondeu || c.desistiu}
                                                    onChange={() => marcarPresenca(c.id, !c.presenca)}
                                                    style={{ width: '17px', height: '17px', accentColor: '#059669', cursor: (c.respondeu || c.desistiu) ? 'not-allowed' : 'pointer', margin: 0 }}
                                                />
                                                Presente
                                            </label>
                                            {/* QUEM liberou este candidato para a prova, e quando.
                                                A presença é o porteiro (sem ela a prova não abre),
                                                e um clique em "marcar todos" vale por uma sala —
                                                o rastro precisa estar visível, não só no banco.
                                                Presenças anteriores a 2026-07-16 não têm carimbo:
                                                aí não mostramos nada, em vez de inventar autor. */}
                                            {c.presenca && c.presencaPor && (
                                                <div style={{ fontSize: '11px', color: CORES.textoFraco, marginTop: '3px' }}>
                                                    por {c.presencaPor}
                                                    {c.presencaEm && <span style={{ fontVariantNumeric: 'tabular-nums' }}> · {c.presencaEm}</span>}
                                                </div>
                                            )}
                                        </td>
                                        <td style={{ ...estilos.td, fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>
                                            {!c.respondeu
                                                ? <span style={{ color: CORES.textoFraco, fontWeight: 400 }}>não fez</span>
                                                : c.nota === null
                                                    ? <span style={{ color: '#b45309' }} title="Todas as questões foram anuladas">sem questão válida</span>
                                                    : (
                                                        <>
                                                            {/* Nota 0–10 (cada questão vale 1). Verde + "apto" quando
                                                                alcança o corte de aprovação (≥ 7). A decisão é do RH. */}
                                                            <span style={{ color: c.nota >= NOTA_CORTE ? '#059669' : CORES.texto }}>{c.nota}</span>
                                                            {c.nota >= NOTA_CORTE && <span style={aptoTag}>apto</span>}
                                                            <div style={{ fontSize: '11px', color: CORES.textoFraco, fontWeight: 400 }}
                                                                title={`${c.respondidas} de ${dados.prova.qtdValidas} respondida(s) — questão em branco conta como erro`}>
                                                                {c.acertosQtd}/{dados.prova.qtdValidas} certas
                                                            </div>
                                                        </>
                                                    )}
                                        </td>
                                        <td style={estilos.td}>
                                            {/* Desistiu substitui o estado da prova: decidir quem saiu
                                                do processo o mandaria para a fila da LIVE, onde o
                                                Treinamento passaria 30 dias esperando avaliá-lo. */}
                                            {c.desistiu
                                                ? <MarcaDesistiu motivo={c.desistencia?.motivo} largura="220px" />
                                                : <>
                                                    {c.resultado === 'Aprovado' && <BadgeStatus tom="aprovado">Aprovado</BadgeStatus>}
                                                    {c.resultado === 'Reprovado' && <BadgeStatus tom="reprovado">Reprovado</BadgeStatus>}
                                                    {!c.resultado && <BadgeStatus tom="rascunho">Pendente</BadgeStatus>}
                                                </>}
                                        </td>
                                        <td style={{ ...estilos.td, textAlign: 'center', whiteSpace: 'nowrap' }}>
                                            {c.desistiu ? (
                                                <BotaoDesfazerDesistencia
                                                    idCandidato={c.id} nome={c.nome} compacto
                                                    onPronto={() => carregarRanking(idVaga)} onErro={setErro} />
                                            ) : (
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '2px' }}>
                                                    <button onClick={() => decidir(c.id, 'Aprovado')} disabled={!c.respondeu}
                                                        title={c.respondeu ? 'Aprovar' : 'Não fez a prova'} style={{ ...btnAprovar, opacity: c.respondeu ? 1 : 0.4 }}>
                                                        <CheckCircle size={18} />
                                                    </button>
                                                    <button onClick={() => decidir(c.id, 'Reprovado')} disabled={!c.respondeu}
                                                        title={c.respondeu ? 'Reprovar' : 'Não fez a prova'} style={{ ...btnReprovar, opacity: c.respondeu ? 1 : 0.4 }}>
                                                        <XCircle size={18} />
                                                    </button>
                                                    {/* Depois de decidido não cabe desistir aqui: quem foi
                                                        aprovado desiste na LIVE, e quem foi reprovado já saiu. */}
                                                    {!c.resultado && <BotaoDesistir onClick={() => setDesistindo(c)} />}
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                                {!dados.ranking.length && (
                                    <tr><td style={estilos.td} colSpan={8}>Nenhum candidato aprovado na triagem para esta vaga.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Observação DESTACADA — as regras que o RH precisa ter à vista. */}
                    <div style={obsBox}>
                        <p style={{ margin: 0 }}>
                            <strong>Média para estar apto à aprovação: nota ≥ {String(NOTA_CORTE).replace('.', ',')},0 pontos</strong> (escala 0 a 10).
                            Cada prova pode ter uma quantidade de questões diferente — a nota é sempre a <strong>média</strong>, então o corte de {NOTA_CORTE} vale para todas.
                        </p>
                        <p style={{ margin: '8px 0 0 0' }}>
                            <strong>Empate na nota</strong> é desfeito por <strong>tempo de casa</strong> (admissão mais antiga na frente).
                            Quem você <strong>aprovar</strong> aqui é quem vai para a <strong>LIVE</strong>, 30 dias depois.
                        </p>
                        <p style={{ margin: '8px 0 0 0' }}>
                            Clique no <strong>nome</strong> de quem fez a prova para ver a <strong>correção</strong> (níveis, acertos e erros).
                        </p>
                    </div>
                </>
            )}

            {(carregandoDet || detalhe || erroDet) && (
                <PainelProvaCandidato
                    carregando={carregandoDet}
                    erro={erroDet}
                    dados={detalhe}
                    onFechar={() => { setDetalhe(null); setErroDet(''); }}
                />
            )}

            {desistindo && (
                <ModalDesistencia
                    idCandidato={desistindo.id} nome={desistindo.nome} detalhe={dados?.vaga?.cargo}
                    onFechar={() => setDesistindo(null)}
                    onPronto={() => { setDesistindo(null); carregarRanking(idVaga); }} />
            )}

            {incluindo && (
                <ModalIncluirCandidato
                    idVaga={idVaga} cargoVaga={dados?.vaga?.cargo}
                    onFechar={() => setIncluindo(false)}
                    // A mensagem vem do servidor porque é ele quem sabe o que de fato
                    // aconteceu (criou, reconvocou um reprovado, ou caiu em outra vaga).
                    onIncluido={(mensagem) => { setMsg(mensagem); setErro(''); carregarRanking(idVaga); }} />
            )}
        </div>
    );
}

// ─── Painel: a PROVA CORRIGIDA de um candidato ───────────────────────────────
// Mostra, por questão, o nível e o peso, o que o candidato marcou e o gabarito —
// e se acertou / errou / deixou em branco. É o que torna a nota (ponderada por
// nível) compreensível: "errou 1 Difícil (3 pts)" explica mais que "80% de acerto".
const TOM_RESULTADO = {
    acertou: { rotulo: 'Certa', tom: 'aprovado' },
    errou: { rotulo: 'Errada', tom: 'reprovado' },
    branco: { rotulo: 'Em branco', tom: 'rascunho' },
    anulada: { rotulo: 'Anulada', tom: 'alerta' },
};

function PainelProvaCandidato({ carregando, erro, dados, onFechar }) {
    return (
        <div style={overlay} onClick={onFechar}>
            <div style={painel} onClick={(e) => e.stopPropagation()}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', marginBottom: '4px' }}>
                    <div>
                        <h4 style={{ fontSize: '17px', fontWeight: 800, color: CORES.texto, margin: 0 }}>
                            {dados ? `Prova de ${dados.candidato.nome}` : 'Prova do candidato'}
                        </h4>
                        {dados && (
                            <p style={{ fontSize: '12px', color: CORES.textoFraco, margin: '4px 0 0 0' }}>
                                {dados.candidato.vaga}{dados.candidato.loja ? ` · ${dados.candidato.loja}` : ''}
                            </p>
                        )}
                    </div>
                    <button onClick={onFechar} style={{ background: 'none', border: 'none', cursor: 'pointer', color: CORES.textoFraco }}>
                        <X size={20} />
                    </button>
                </div>

                {carregando && <p style={{ color: CORES.textoFraco }}>Carregando…</p>}
                {erro && <p style={{ color: '#be123c' }}>{erro}</p>}

                {dados && !dados.respondeu && (
                    <p style={{ color: '#b45309', fontSize: '13px' }}>Este candidato ainda não entregou a prova.</p>
                )}

                {dados && dados.respondeu && (
                    <>
                        {/* Resumo — a nota e de onde ela vem (certas/erros/branco + pontos). */}
                        <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap', backgroundColor: CORES.primariaBg, borderRadius: '12px', padding: '14px 16px', margin: '10px 0 18px 0' }}>
                            <div style={{ textAlign: 'center', minWidth: '70px' }}>
                                <div style={{ fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', color: CORES.primaria }}>Nota</div>
                                <div style={{ fontSize: '28px', fontWeight: 900, color: CORES.primaria, fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 }}>
                                    {dados.resumo.nota === null ? '—' : dados.resumo.nota}
                                </div>
                            </div>
                            <div style={{ fontSize: '13px', color: CORES.texto, lineHeight: 1.7 }}>
                                <div>
                                    <strong style={{ color: '#059669' }}>{dados.resumo.acertosQtd} certa(s)</strong> ·{' '}
                                    <strong style={{ color: '#e11d48' }}>{dados.resumo.errosQtd} errada(s)</strong> ·{' '}
                                    <strong style={{ color: '#b45309' }}>{dados.resumo.brancosQtd} em branco</strong>
                                    {dados.resumo.qtdAnuladas > 0 && <> · {dados.resumo.qtdAnuladas} anulada(s)</>}
                                    {' '}de {dados.resumo.qtdValidas} válida(s)
                                </div>
                                <div style={{ color: CORES.textoFraco }}>
                                    nota = acertos ÷ questões válidas × 10 · questão em branco conta como erro
                                </div>
                                {dados.resumo.nota !== null && (
                                    <div style={{ fontSize: '12px', fontWeight: 800, color: dados.resumo.nota >= NOTA_CORTE ? '#059669' : '#b45309' }}>
                                        {dados.resumo.nota >= NOTA_CORTE ? `Apto — nota ≥ ${NOTA_CORTE}` : `Abaixo do corte — nota < ${NOTA_CORTE}`}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Uma linha por questão, com nível, peso, gabarito e a marcação do candidato. */}
                        {dados.questoes.map((q, i) => {
                            const res = TOM_RESULTADO[q.resultado] || TOM_RESULTADO.branco;
                            return (
                                <div key={q.id} style={{
                                    border: `1px solid ${CORES.borda}`, borderRadius: '12px', padding: '14px',
                                    marginBottom: '10px', opacity: q.anulada ? 0.7 : 1,
                                }}>
                                    <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                                        <span style={{ fontWeight: 900, color: CORES.primaria, minWidth: '22px' }}>{i + 1}.</span>
                                        <div style={{ flex: 1, minWidth: '200px' }}>
                                            <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '6px' }}>
                                                {q.nivel && <BadgeStatus tom={TOM_NIVEL[q.nivel] || 'neutro'}>{q.nivel}</BadgeStatus>}
                                                <span style={{ fontSize: '11px', color: CORES.textoFraco }}>{q.area}</span>
                                                <BadgeStatus tom={res.tom}>{res.rotulo}</BadgeStatus>
                                            </div>
                                            <p style={{ margin: 0, fontSize: '14px', color: CORES.texto, lineHeight: 1.5 }}>{q.texto}</p>
                                        </div>
                                    </div>

                                    <div style={{ paddingLeft: '32px', marginTop: '8px' }}>
                                        {q.alternativas.map((a) => {
                                            // Verde = gabarito; vermelho = o candidato marcou e ERROU.
                                            const ehGabarito = a.correta;
                                            const marcouErrado = a.marcada && !a.correta;
                                            return (
                                                <div key={a.id} style={{
                                                    display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '4px',
                                                    padding: '4px 8px', borderRadius: '6px',
                                                    backgroundColor: ehGabarito ? '#ecfdf5' : (marcouErrado ? '#fff1f2' : 'transparent'),
                                                }}>
                                                    <span style={{
                                                        fontSize: '13px', flex: 1,
                                                        color: ehGabarito ? '#047857' : (marcouErrado ? '#be123c' : CORES.textoSec),
                                                        fontWeight: (ehGabarito || a.marcada) ? 700 : 400,
                                                    }}>
                                                        {a.texto}
                                                    </span>
                                                    {a.marcada && (
                                                        <span style={{ fontSize: '11px', fontWeight: 800, color: a.correta ? '#059669' : '#e11d48' }}>
                                                            {a.correta ? '✓ marcou' : '✗ marcou'}
                                                        </span>
                                                    )}
                                                    {ehGabarito && !a.marcada && (
                                                        <span style={{ fontSize: '11px', fontWeight: 700, color: '#059669' }}>gabarito</span>
                                                    )}
                                                </div>
                                            );
                                        })}
                                        {q.resultado === 'branco' && (
                                            <p style={{ fontSize: '12px', color: '#b45309', margin: '4px 0 0 0' }}>Não respondeu esta questão.</p>
                                        )}
                                        {q.anulada && (
                                            <p style={{ fontSize: '12px', color: '#b45309', margin: '4px 0 0 0' }}>Questão anulada — não conta para ninguém.</p>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </>
                )}
            </div>
        </div>
    );
}

const btnAcao = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '7px', borderRadius: '8px', backgroundColor: '#fff', cursor: 'pointer', margin: '0 3px' };
const btnAprovar = { ...btnAcao, border: '1px solid #a7f3d0', color: '#059669' };
const btnReprovar = { ...btnAcao, border: '1px solid #fbb6c4', color: '#e11d48' };

// Selo "apto" ao lado da nota quando alcança o corte (≥ 7).
const aptoTag = {
    marginLeft: '6px', fontSize: '10px', fontWeight: 800, color: '#059669',
    backgroundColor: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: '999px', padding: '1px 6px',
};
// Caixa da observação destacada (regras da aprovação/desempate/correção).
const obsBox = {
    marginTop: '14px', backgroundColor: '#fff7ed', border: '1px solid #fed7aa',
    borderRadius: '10px', padding: '12px 14px', fontSize: '13px', color: '#7c2d12', lineHeight: 1.5,
};

// A sala: quais processos dividem o mesmo QR. Fundo sutil para se ler como parte
// da caixa de vagas, não como um bloco novo.
const caixaSala = {
    backgroundColor: '#f8fafc', border: `1px solid ${CORES.borda}`,
    borderRadius: '10px', padding: '12px 14px', marginBottom: '14px',
};

// Nome clicável (abre a prova corrigida) — parece link, alinhado com o texto.
const btnNome = {
    display: 'inline-flex', alignItems: 'center', gap: '5px', background: 'none', border: 'none',
    padding: 0, cursor: 'pointer', color: CORES.primaria, font: 'inherit', textAlign: 'left',
};
const overlay = {
    position: 'fixed', inset: 0, backgroundColor: 'rgba(15, 23, 42, 0.45)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', zIndex: 1000,
};
const painel = {
    backgroundColor: '#fff', borderRadius: '14px', padding: '22px',
    width: '100%', maxWidth: '720px', maxHeight: '88vh', overflowY: 'auto',
    boxShadow: '0 20px 50px -12px rgba(0,0,0,0.3)',
};

export default PPIAplicacao;
